import type { FC } from 'react';
import React, { useMemo, Fragment } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	getMacroAttributesFromADFNode,
	getExperienceName,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStop } from '@confluence/macro-tracker/entry-points/MacroExperienceStop.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { fg } from '@confluence/feature-gating';

import { ExcerptIncludeComponent } from './ExcerptIncludeComponent.tsx';
import { ExcerptIncludeComponentShadow } from './ExcerptIncludeComponentShadow.tsx';
import { ExcerptIncludeError } from './ExcerptIncludeError.tsx';
import type { ExcerptIncludeProps } from './types.ts';
import { getMacroId, getMacroParam, parseMacroOutputAdf, injectParentIdIntoAdf } from './utils.ts';
import { useExperimentalMacroMetadataQuery } from './useExperimentalMacroMetadataQuery.ts';

/**
 * Component that is responsible for managing a state requred for
 * excerpt-include macro. It could either:
 * - Try to look for data in macroOutput param of the macro, if not present then:
 * - Query GraphQL enpoint and fetche the excerpt data from the target page.
 * The actual rendering is happening is delegated to ExcerptIncludeComponent.
 */
export const ExcerptIncludeImpl: FC<ExcerptIncludeProps> = ({
	contentId,
	spaceKey,
	extensionKey,
	macroNode,
	mode,
	RendererWrapper,
	isLegacyRenderer,
	isLivePage,
}) => {
	const {
		extensionType,
		parameters: { macroMetadata, macroParams, macroOutput, mediaToken },
		type,
	} = macroNode;

	const attributes = getMacroAttributesFromADFNode(macroNode);
	const renderServerPlaceholder = useIsSSROrHydrating();
	const adfFromMacroOutput = useMemo(
		() => parseMacroOutputAdf(macroOutput)?.content,
		[macroOutput],
	);

	const { data, loading, error } = useExperimentalMacroMetadataQuery({
		variables: {
			contentId,
			macroNode: {
				extensionKey: extensionKey ?? '',
				extensionType,
				parameters: {
					macroMetadata: JSON.stringify(macroMetadata),
					macroParams: JSON.stringify(macroParams),
				},
				type: type ?? '',
			},
		},
		// Avoid to do a query if content already provided in `macroOutput` param
		skip: !!adfFromMacroOutput,
	});

	const targetContentTitle = getMacroParam(macroNode, '') || '';
	const shouldPanelBeDisplayed = getMacroParam(macroNode, 'nopanel') !== 'true';
	const displayInline = getMacroParam(macroNode, 'inline') === 'true';
	const excerptName = getMacroParam(macroNode, 'name');
	const macroId = getMacroId(macroNode);
	const targetContentId =
		data?.experimentalMacroMetadata?.contentId ||
		getMacroParam(macroNode, '_referencedContentId') ||
		getMacroParam(macroNode, '_parentId') ||
		contentId ||
		'';
	const adfString = data?.experimentalMacroMetadata?.payload;
	const parsedAdf = useMemo(() => (adfString ? JSON.parse(adfString) : undefined), [adfString]);
	const adf = adfFromMacroOutput ?? parsedAdf;
	const adfWithParentId = useMemo(
		() =>
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			adf && targetContentId !== contentId && fg('send_parent_id_in_macro_query')
				? injectParentIdIntoAdf(adf, targetContentId)
				: adf,
		[adf, contentId, targetContentId],
	);

	const props = {
		RendererWrapper,
		extensionKey,
		macroNode,
		mode,
		pageId: contentId,
		spaceKey,
		shouldPanelBeDisplayed,
		displayInline,
		excerptName,
		targetContentTitle,
		macroId,
		isLegacyRenderer,
		isLivePage,
	};

	if (adfFromMacroOutput) {
		return (
			<ExcerptIncludeComponent
				{...props}
				adf={adfWithParentId}
				contentId={targetContentId}
				mediaToken={mediaToken}
				renderServerPlaceholder={renderServerPlaceholder}
			/>
		);
	}

	// This point will be reached if macroOutput param does not contain target excerpt data.
	if (loading) {
		return (
			<ExcerptIncludeComponentShadow
				shouldPanelBeDisplayed={shouldPanelBeDisplayed}
				targetContentTitle={targetContentTitle}
			/>
		);
	}

	if (error) {
		const queryError =
			error ||
			new ApolloErrorInterop({
				errorMessage: `${extensionKey} failed to render because macro metadata payload is empty`,
			});
		return (
			<Fragment>
				<MacroExperienceStop
					error={queryError}
					mode={mode}
					name={getExperienceName(mode, macroNode)}
					attributes={attributes}
					contentId={contentId}
					source="ExcerptIncludeImpl"
				/>
				<ExcerptIncludeError targetContentTitle={targetContentTitle} />
			</Fragment>
		);
	}

	return <ExcerptIncludeComponent {...props} adf={adfWithParentId} contentId={targetContentId} />;
};
